/**
 * PatientPortal.tsx — the signed-in PATIENT portal shell (PATIENT-gated).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: UX Wave 10 (2026-07-22);
 * MOBILE-RESPONSIVE shell 2026-07-25.
 *
 * DESKTOP: STAFF-CHROME PARITY (CTO ask, 2026-07-30 — "the same header and
 * sidebar as admin/clinician"): the deep-green section rail owns the left edge
 * (uppercase "Menu" eyebrow over the items, collapse control at the foot), and
 * the working pane wears the SAME white masthead bar the staff consoles use —
 * the shared `StaffMasthead` (logo + "Patient portal" + the per-section
 * breadcrumb) on the left and the shared `ProfileMenu` avatar (Your profile /
 * Sign out) on the right. The old rail-foot account block + Sign out are GONE
 * on desktop — the ProfileMenu is the one account control, exactly like the
 * staff consoles. The working pane stays capped + centred at 1600px.
 * MOBILE (≤860px): the masthead hides, the patient-journey green header
 * (logo + RIGHT-hand hamburger) returns, and the drawer foot shows the
 * account identity with an ALWAYS-VISIBLE Sign out button (no reveal step);
 * the drawer closes via the SVG cross, the scrim, or picking a section. The
 * R31 emergency ribbon (pinned by AppShell) sits above both.
 */
import { useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { BrandLockup } from '../ui/kit';
import { IconCollapse, IconExpand } from '../ui/icons';
import { StaffMasthead } from '../ui/StaffMasthead';
import { ProfileMenu } from '../ui/chrome';
import { Dashboard } from '../patients/Dashboard';
import { ProfilePage } from '../patients/ProfilePage';
import { PATIENT_RAIL_ITEMS, type PatientSection } from './PatientRail';
import { PlanSection } from './PlanSection';
import { OrdersSection } from './OrdersSection';
import { FormsSection } from './FormsSection';

const QUERY_SECTIONS: PatientSection[] = ['dashboard', 'plan', 'orders', 'forms'];

function isQuerySection(value: string | null): value is PatientSection {
  return value !== null && (QUERY_SECTIONS as string[]).includes(value);
}

/** Where each rail item lives — Profile is its own route; the rest are `?section=` on /patients. */
function pathForSection(key: PatientSection): string {
  if (key === 'profile') return '/patients/profile';
  if (key === 'dashboard') return '/patients';
  return `/patients?section=${key}`;
}

const RAIL_BG = 'linear-gradient(180deg, #0f3d2c 0%, #0b2e21 100%)';

const SHELL_CSS = `
.gps-pdash { display: flex; flex-direction: column; flex: 1; min-height: 0;
  /* The R31 emergency ribbon is pinned above this shell (chrome.tsx StickyTop,
   * top: 0, z-index: 50). Every sticky element in here must stack BELOW it, not
   * behind it — EmergencyBar publishes its MEASURED height as
   * --gps-emergency-h on the root (it wraps at narrow widths and shrinks at
   * ≤560px, so it is not a constant). The 32px fallback is the one-line
   * desktop height, used only for the pre-measure first paint. */
  --gps-emergency-offset: var(--gps-emergency-h, 32px);
}

/* MOBILE header — hidden on desktop. */
.gps-pdash-topbar { display: none; }
.gps-pdash-burger {
  display: inline-flex; align-items: center; justify-content: center;
  width: 44px; height: 44px; margin-right: -8px; padding: 0;
  border: none; background: transparent; color: var(--gps-on-accent); cursor: pointer; border-radius: var(--gps-r-md);
}

.gps-pdash-body { display: flex; align-items: stretch; flex: 1; min-height: 0; }

/* DESKTOP full-height sidebar. */
.gps-pdash-rail {
  flex: none; width: 236px; align-self: flex-start;
  position: sticky; top: var(--gps-emergency-offset); height: calc(100vh - var(--gps-emergency-offset)); box-sizing: border-box;
  background: ${RAIL_BG}; padding: var(--gps-s-4) var(--gps-s-2);
  display: flex; flex-direction: column; gap: var(--gps-s-3);
  transition: width var(--gps-dur) var(--gps-ease); overflow: hidden;
}
.gps-pdash-rail[data-collapsed="true"] { width: 72px; }
.gps-pdash-rail-foot { margin-top: auto; border-top: 1px solid rgba(255,255,255,0.18); padding-top: var(--gps-s-2); display: flex; flex-direction: column; gap: 2px; }

/* The working column to the RIGHT of the rail: the white masthead bar (staff
 * parity) pinned on top, the scrolling main under it. */
.gps-pdash-pane { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.gps-pdash-masthead {
  min-height: 56px; background: var(--gps-surface); border-bottom: 1px solid var(--gps-border);
  position: sticky; top: var(--gps-emergency-offset); z-index: 20; box-sizing: border-box;
}
.gps-pdash-masthead-inner {
  display: flex; justify-content: space-between; align-items: center; gap: var(--gps-s-3);
  width: 100%; max-width: 1600px; margin: 0 auto; min-height: 56px;
  padding: 8px clamp(16px, 4vw, 40px); box-sizing: border-box;
}
.gps-pdash-main { flex: 1; min-width: 0; width: 100%; max-width: 1600px; margin: 0 auto;
  padding: var(--gps-s-5) clamp(16px, 4vw, 40px) var(--gps-s-6); box-sizing: border-box; }

/* Mobile nav drawer — slides in from the RIGHT (smooth), fading scrim; always in
 * the DOM so it animates, aria-hidden + off-screen when closed. */
.gps-pdash-scrim {
  position: fixed; inset: 0; background: rgba(19,35,27,0.5); z-index: 40;
  opacity: 0; pointer-events: none; transition: opacity 0.25s ease;
}
.gps-pdash-scrim[data-open="true"] { opacity: 1; pointer-events: auto; }
.gps-pdash-drawer {
  position: fixed; top: 0; right: 0; bottom: 0; width: min(84vw, 300px); z-index: 41;
  background: ${RAIL_BG}; padding: var(--gps-s-3) var(--gps-s-2); box-sizing: border-box; overflow-y: auto;
  display: flex; flex-direction: column; gap: var(--gps-s-1);
  transform: translateX(100%); visibility: hidden;
  transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1), visibility 0s 0.28s;
  box-shadow: -8px 0 28px rgba(19, 35, 27, 0.28);
}
.gps-pdash-drawer[data-open="true"] {
  transform: translateX(0); visibility: visible;
  transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1), visibility 0s 0s;
}
.gps-pdash-drawer-head { display: flex; align-items: center; justify-content: flex-end; padding: 0 4px 4px; }
.gps-pdash-drawer-foot { margin-top: auto; border-top: 1px solid rgba(255,255,255,0.18); padding-top: var(--gps-s-2); }
@media (prefers-reduced-motion: reduce) { .gps-pdash-scrim, .gps-pdash-drawer { transition: none; } }

@media (max-width: 860px) {
  .gps-pdash-rail { display: none; }
  .gps-pdash-masthead { display: none; }
  .gps-pdash-topbar {
    display: flex; align-items: center; gap: var(--gps-s-3);
    min-height: 56px; box-sizing: border-box; padding: 8px clamp(16px, 4vw, 40px);
    background: var(--gps-header-bg); position: sticky; top: var(--gps-emergency-offset); z-index: 20;
  }
}
`;

function BurgerIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" width={25} height={25} style={{ display: 'block' }}>
      <path fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" d="M3.5 7h17M3.5 12h17M3.5 17h17" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" width={22} height={22} style={{ display: 'block' }}>
      <path fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

function SignOutIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 21H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3" />
      <path d="M16 17l5-5-5-5" />
      <path d="M21 12H9" />
    </svg>
  );
}

const railLinkStyle = (active: boolean, collapsed: boolean) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: collapsed ? 'center' : 'flex-start',
  gap: '12px',
  width: '100%',
  textAlign: 'left' as const,
  minHeight: '44px',
  padding: collapsed ? '10px 0' : '10px 12px',
  background: active ? 'rgba(255, 255, 255, 0.16)' : 'none',
  border: 0,
  borderLeft: `3px solid ${active ? 'var(--gps-on-accent)' : 'transparent'}`,
  borderRadius: 'var(--gps-r-sm)',
  color: active ? 'var(--gps-on-accent)' : 'rgba(255, 255, 255, 0.82)',
  font: 'inherit',
  fontSize: '14.5px',
  fontWeight: active ? 600 : 500,
  cursor: 'pointer',
  whiteSpace: 'nowrap' as const,
  textDecoration: 'none',
});

const iconSlot = { width: '20px', flex: 'none' as const, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' };

/**
 * The section list — shared by the desktop rail and the mobile drawer. Both
 * pass `showEyebrow` for the "Menu" eyebrow (staff-chrome parity, 2026-07-30 —
 * the rail lost its logo + account block to the masthead/ProfileMenu, so the
 * eyebrow is what labels it); the eyebrow hides while the rail is collapsed.
 */
function SectionNav({ activeKey, onSelect, collapsed = false, showEyebrow = false }: { activeKey: PatientSection; onSelect: (key: PatientSection) => void; collapsed?: boolean; showEyebrow?: boolean }) {
  return (
    <>
      {showEyebrow && !collapsed ? (
        <div aria-hidden="true" style={{ margin: 'var(--gps-s-1) var(--gps-s-2) var(--gps-s-2)', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(255, 255, 255, 0.55)' }}>
          Menu
        </div>
      ) : null}
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
        {PATIENT_RAIL_ITEMS.map(({ key, label, icon }) => {
          const active = key === activeKey;
          return (
            <li key={key}>
              <button type="button" className="gps-rail-link" aria-current={active ? 'page' : undefined} title={collapsed ? label : undefined} onClick={() => onSelect(key)} style={railLinkStyle(active, collapsed)}>
                <span aria-hidden="true" style={iconSlot}>{icon}</span>
                <span className={collapsed ? 'gps-sr-only' : undefined}>{label}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </>
  );
}

/**
 * The mobile drawer's account foot: the signed-in identity (avatar initial +
 * name/email, non-interactive) over an ALWAYS-VISIBLE Sign out button
 * (2026-07-30 — one tap, no reveal step; the drawer is the only mobile
 * account surface, so the action must never hide behind the email).
 */
function DrawerAccount() {
  const { me, logout } = useAuth();
  const name = me?.fullName ?? me?.email ?? '';
  const initial = (name || '•').charAt(0).toUpperCase();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', color: 'rgba(255,255,255,0.82)', fontSize: '14.5px' }}>
        <span aria-hidden="true" style={{ width: '26px', height: '26px', flex: 'none', borderRadius: '50%', background: 'rgba(255,255,255,0.16)', color: 'var(--gps-on-accent)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 700 }}>
          {initial}
        </span>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
      </div>
      <button type="button" className="gps-rail-link" onClick={() => logout()} style={railLinkStyle(false, false)}>
        <span aria-hidden="true" style={iconSlot}><SignOutIcon /></span>
        Sign out
      </button>
    </div>
  );
}

export function PatientPortal() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [navOpen, setNavOpen] = useState(false); // mobile drawer
  const [collapsed, setCollapsed] = useState(false); // desktop rail

  const onProfile = location.pathname.startsWith('/patients/profile');
  const sectionParam = searchParams.get('section');
  const active: PatientSection = onProfile ? 'profile' : isQuerySection(sectionParam) ? sectionParam : 'dashboard';
  // The masthead breadcrumb's current crumb — the active section's label
  // (staff parity: "Patient portal › Dashboard").
  const activeLabel = PATIENT_RAIL_ITEMS.find((i) => i.key === active)?.label ?? 'Dashboard';

  function select(key: PatientSection) {
    setNavOpen(false); // a mobile drawer selection closes the drawer
    if (key === active) return;
    navigate(pathForSection(key), { replace: true });
  }

  return (
    <div className="gps-pdash">
      <style>{SHELL_CSS}</style>

      {/* MOBILE header (≤860px) — desktop uses the sidebar's own logo/account instead. */}
      <header className="gps-pdash-topbar">
        <Link to="/patients" aria-label="GP Service — patient home" style={{ display: 'inline-flex', textDecoration: 'none' }}>
          <BrandLockup />
        </Link>
        <div style={{ flex: 1 }} />
        <button type="button" className="gps-pdash-burger" aria-label="Open menu" aria-expanded={navOpen} aria-controls="gps-pdash-drawer" onClick={() => setNavOpen(true)}>
          <BurgerIcon />
        </button>
      </header>

      <div className="gps-pdash-body">
        {/* DESKTOP full-height sidebar — staff parity: the logo lives on the
            white masthead (Wave 7B), the rail carries the Menu eyebrow + items
            and the collapse control only. Account moved to the ProfileMenu. */}
        <nav className="gps-pdash-rail" data-collapsed={collapsed} aria-label="Patient sections">
          <SectionNav activeKey={active} onSelect={select} collapsed={collapsed} showEyebrow />

          <div className="gps-pdash-rail-foot">
            <button
              type="button"
              className="gps-rail-link"
              aria-expanded={!collapsed}
              aria-label={collapsed ? 'Expand the menu' : 'Collapse the menu'}
              onClick={() => setCollapsed((v) => !v)}
              style={railLinkStyle(false, collapsed)}
            >
              <span aria-hidden="true" style={iconSlot}>{collapsed ? <IconExpand /> : <IconCollapse />}</span>
              {!collapsed ? <span aria-hidden="true">Collapse</span> : null}
            </button>
          </div>
        </nav>

        <div className="gps-pdash-pane">
          {/* DESKTOP white masthead — the SAME process as the staff consoles:
              shared StaffMasthead (logo + workspace + section breadcrumb) left,
              shared role-aware ProfileMenu (Your profile / Sign out) right. */}
          <header className="gps-pdash-masthead">
            <div className="gps-pdash-masthead-inner">
              <StaffMasthead
                workspace="Patient portal"
                section={activeLabel}
                homeTo="/patients"
                homeLabel="GP Service — patient home"
              />
              <ProfileMenu />
            </div>
          </header>

          <main className="gps-pdash-main">
            {active === 'dashboard' ? <Dashboard /> : null}
            {active === 'plan' ? <PlanSection /> : null}
            {active === 'orders' ? <OrdersSection /> : null}
            {active === 'forms' ? <FormsSection /> : null}
            {active === 'profile' ? <ProfilePage /> : null}
          </main>
        </div>
      </div>

      {/* MOBILE drawer (always rendered so it can slide; a11y-hidden when closed). */}
      <div className="gps-pdash-scrim" data-open={navOpen} aria-hidden={!navOpen} onClick={() => setNavOpen(false)} />
      <nav id="gps-pdash-drawer" className="gps-pdash-drawer" data-open={navOpen} aria-hidden={!navOpen} aria-label="Patient sections menu">
        <div className="gps-pdash-drawer-head">
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setNavOpen(false)}
            style={{ width: '44px', height: '44px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: 'none', background: 'transparent', color: 'var(--gps-on-accent)', cursor: 'pointer', borderRadius: 'var(--gps-r-md)' }}
          >
            <CloseIcon />
          </button>
        </div>
        <SectionNav activeKey={active} onSelect={select} showEyebrow />
        <div className="gps-pdash-drawer-foot">
          <DrawerAccount />
        </div>
      </nav>
    </div>
  );
}
